import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '../ui/Button';
import Input from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;

const PasswordReset: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'form' | 'success' | 'error' | 'expired'>('loading');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [token, setToken] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const resetToken = urlParams.get('token');

    if (!resetToken) {
      setStatus('error');
      setMessage('Invalid password reset link. No token provided.');
      return;
    }

    setToken(resetToken);
    setStatus('form');
  }, [location.search]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!token) return;

    try {
      setIsSubmitting(true);
      setMessage('');

      const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        token,
        password: formData.password
      });

      if (response.data.success) {
        setStatus('success');
        setMessage('Password reset successfully! You can now log in with your new password.');
        
        // Redirect to home page after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(response.data.message || 'Password reset failed');
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      if (error.response?.status === 400) {
        setStatus('expired');
        setMessage(error.response.data.message || 'Password reset link has expired or is invalid');
      } else {
        setStatus('error');
        setMessage('Password reset failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return '⏳';
      case 'form':
        return '🔐';
      case 'success':
        return '✅';
      case 'error':
      case 'expired':
        return '❌';
      default:
        return '🔐';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
      case 'expired':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-blue-600 dark:text-blue-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8"
      >
        <div className="text-center">
          <div className="text-6xl mb-4">
            {status === 'loading' ? (
              <LoadingSpinner size="lg" className="mx-auto" />
            ) : (
              getStatusIcon()
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {status === 'loading' && 'Loading...'}
            {status === 'form' && 'Reset Your Password'}
            {status === 'success' && 'Password Reset!'}
            {status === 'error' && 'Reset Failed'}
            {status === 'expired' && 'Link Expired'}
          </h1>

          {message && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`mb-6 ${getStatusColor()}`}
            >
              <p className="text-sm">{message}</p>
            </motion.div>
          )}

          <div className="space-y-4">
            {status === 'form' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  name="password"
                  type="password"
                  label="New Password"
                  placeholder="Enter your new password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={errors.password}
                  disabled={isSubmitting}
                  helperText="8+ characters with uppercase, lowercase, number, and special character"
                />
                
                <Input
                  name="confirmPassword"
                  type="password"
                  label="Confirm New Password"
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={errors.confirmPassword}
                  disabled={isSubmitting}
                />

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {status === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
              >
                <p className="text-green-600 dark:text-green-400 text-sm">
                  🎉 You can now log in with your new password! You'll be redirected shortly.
                </p>
              </motion.div>
            )}

            {(status === 'error' || status === 'expired') && (
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="w-full text-sm"
              >
                ← Back to Home
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordReset;
